/*
 * Lazy content for "Product Details" match drawers (snippets/match-product-drawer.liquid).
 *
 * The page only contains a drawer shell with data-match-drawer-src. The full drawer is fetched from
 * sections/match-product-drawer-content.liquid when a toggle ([aria-controls="<drawer id>"]) is hovered,
 * focused, touched or clicked, then its children / styles / scripts are inserted into the shell.
 * The fetched markup uses the __MPD__ token as drawer id; it is replaced with the shell's id first.
 */
(function () {
  if (window.matchDrawerLazy) return;

  var TOKEN = /__MPD__/g;
  var pending = {};

  function getShell(id) {
    var el = id && document.getElementById(id);
    return el && el.hasAttribute('data-match-drawer-src') ? el : null;
  }

  function fetchContent(shell) {
    var id = shell.id;
    if (!pending[id]) {
      pending[id] = fetch(shell.getAttribute('data-match-drawer-src'), { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          // Parse the fetched <x-drawer> as an inert <div>: a real x-drawer would register its own
          // [aria-controls] click delegate as soon as it is created and open itself next to the shell.
          return html
            .replace(TOKEN, id)
            .replace(/<x-drawer\b/g, '<div data-match-fetched-drawer')
            .replace(/<\/x-drawer>/g, '</div>');
        });
    }
    return pending[id];
  }

  function inject(shell, html) {
    if (shell.dataset.matchDrawerLoaded === '1') return;
    shell.dataset.matchDrawerLoaded = '1';

    // createContextualFragment (unlike innerHTML) keeps scripts executable and creates the nodes in this
    // document, so custom elements (accordion-disclosure, product-form…) upgrade normally once inserted.
    var fragment = document.createRange().createContextualFragment(html);
    var root = fragment.querySelector('.shopify-section') || fragment;
    var drawer = root.querySelector('[data-match-fetched-drawer]');
    if (!drawer) throw new Error('Drawer markup missing');

    // 1) Drawer body/footer into the shell (the shell already has the header and stays the live element).
    var loading = shell.querySelector('[data-match-drawer-loading]');
    Array.prototype.slice.call(drawer.childNodes).forEach(function (node) {
      if (node.nodeType === 1 && node.getAttribute('slot') === 'header') return;
      shell.appendChild(node);
    });
    if (loading) loading.remove();
    drawer.remove();

    // 2) The drawer's own styles / scripts (rendered next to it) — scripts run after the markup exists.
    var assets = document.createElement('div');
    assets.hidden = true;
    assets.setAttribute('data-match-drawer-assets', shell.id);
    Array.prototype.slice.call(root.childNodes).forEach(function (node) {
      assets.appendChild(node);
    });
    document.body.appendChild(assets);
  }

  function load(shell) {
    if (!shell || shell.dataset.matchDrawerLoaded === '1') return;
    fetchContent(shell)
      .then(function (html) {
        inject(shell, html);
      })
      .catch(function () {
        delete pending[shell.id];
        shell.dataset.matchDrawerLoaded = '';
        var fallback = shell.querySelector('[data-match-drawer-fallback]');
        var spinner = shell.querySelector('.match-product-drawer__spinner');
        if (fallback) fallback.hidden = false;
        if (spinner) spinner.hidden = true;
      });
  }

  function shellFromEvent(event) {
    var toggle = event.target && event.target.closest && event.target.closest('[aria-controls]');
    return toggle ? getShell(toggle.getAttribute('aria-controls')) : null;
  }

  // Prefetch only on deliberate hover / focus (pages like Inspiration have 100+ cards, and sweeping the
  // pointer across them while scrolling must not fire a request per card), with limited parallelism.
  // Taps and clicks always load immediately.
  var PREFETCH_DELAY = 200;
  var MAX_PREFETCH = 2;
  var inFlight = 0;
  var hoverTimer = null;

  function prefetch(shell) {
    if (!shell || shell.dataset.matchDrawerLoaded === '1' || pending[shell.id] || inFlight >= MAX_PREFETCH) return;
    inFlight++;
    fetchContent(shell)
      .catch(function () {
        delete pending[shell.id];
      })
      .then(function () {
        inFlight--;
      });
  }

  function onHoverIntent(event) {
    var shell = shellFromEvent(event);
    clearTimeout(hoverTimer);
    if (shell) hoverTimer = setTimeout(prefetch, PREFETCH_DELAY, shell);
  }

  function onActivate(event) {
    load(shellFromEvent(event));
  }

  document.addEventListener('pointerover', function (event) {
    if (event.pointerType === 'mouse') onHoverIntent(event);
  }, { passive: true });
  document.addEventListener('pointerout', function () {
    clearTimeout(hoverTimer);
  }, { passive: true });
  document.addEventListener('focusin', onHoverIntent);
  document.addEventListener('touchstart', onActivate, { passive: true });
  document.addEventListener('click', onActivate, true);

  // Safety net for drawers opened programmatically (no toggle interaction).
  document.addEventListener('dialog:after-show', function (event) {
    load(getShell(event.target && event.target.id));
  });

  window.matchDrawerLazy = { load: load };
})();
